import { zodResolver } from '@hookform/resolvers/zod';
import { type JSX, useCallback, useContext } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod/mini';
import { InputV2 } from '@/app_components/ui/InputV2';
import { SelectV2 } from '@/app_components/ui/SelectV2';
import { FilterBy } from '@/app_data/widgets/RedVentures/utils';
import { GtmContext } from '@/app_lib/providers/GtmProvider';
import { trackEvent } from '@/app_lib/services/Gtm/utils';
import { defaultZipCode } from '@/app_lib/utils/utils';
import {
  maxTermLengths,
  minTermLengths,
  type RatesInfoInputKey,
  savingsAccountTypes,
} from '@/app_lib/utils/widgets/redVenturesConstants';

interface RatesInputProps {
  inputs: RatesInfoInputKey[];
  onChange: (data: FilterBy) => void;
  userZipCode?: string;
}

export const RatesInput = ({ inputs, onChange, userZipCode }: RatesInputProps) => {
  const { pageData } = useContext(GtmContext);

  const form = useForm<FilterBy>({
    defaultValues: {
      accountType: 'MMA,SAVINGS',
      depositAmount: 25000,
      maxTermLength: 36,
      minTermLength: 6,
      zip: userZipCode || defaultZipCode,
    },
    mode: 'onChange',
    resolver: zodResolver(FilterBy),
  });

  const handleFormChange = () => {
    const values = form.getValues();

    if (FilterBy.safeParse(values).success) {
      onChange(values);
    }
  };

  // This is just to show errors on the input fields - not used for validation since race condition
  const result = FilterBy.safeParse(form.getValues());
  const errorTree = result.success ? undefined : z.treeifyError(result.error);

  const minTermLength = form.watch('minTermLength');

  const trackInput = useCallback(
    (action: string) => {
      const trackingData = {
        // biome-ignore lint/style/useNamingConvention: <external data>
        contentPlacementCD: 'Recommends Widget',
        eventAction: action,
        eventCategory: 'Recommends Widget',
      };
      trackEvent({ ...pageData, ...trackingData });
    },
    [pageData],
  );

  const textInput = ({ handleOnChange, value, placeholder, label, id }) => (
    <InputV2
      className='grow w-[calc(50%-16px)]'
      error={errorTree?.properties?.[id]?.errors[0]}
      id={id}
      label={label}
      onChange={handleOnChange}
      placeholder={placeholder}
      required
      type='text'
      value={value}
    />
  );

  const numberInput = ({ handleOnChange, value, placeholder, label, id }) => (
    <div className='relative grow w-[calc(50%-16px)] [&_input]:pl-8'>
      <InputV2
        error={errorTree?.properties?.[id]?.errors[0]}
        id={id}
        label={label}
        min={0}
        onChange={handleOnChange}
        placeholder={placeholder}
        step={1000}
        type='number'
        value={value}
      />
      <span className='absolute left-4 bottom-2.5'>$</span>
    </div>
  );

  const select = ({ handleOnChange, value, placeholder, label, id, options }) => (
    <SelectV2
      className='grow w-[calc(50%-16px)]'
      error={errorTree?.properties?.[id]?.errors[0]}
      id={id}
      label={label}
      onChange={handleOnChange}
      options={options}
      placeholder={placeholder}
      required
      value={value}
    />
  );

  const RatesInfoInputs: Record<RatesInfoInputKey, JSX.Element> = {
    accountType: select({
      handleOnChange: (_id, val) => {
        form.setValue('accountType', val);
        handleFormChange();
        trackInput('Input Interaction Dropdown');
      },
      id: 'accountType',
      label: 'Account Type',
      options: savingsAccountTypes,
      placeholder: 'Account Type',
      value: form.watch('accountType')?.toString() || '',
    }),
    depositAmount: numberInput({
      handleOnChange: (e) => {
        const val = e.target.value;
        form.setValue('depositAmount', Number(val));
        handleFormChange();
        trackInput('Input Interaction Deposit');
      },
      id: 'depositAmount',
      label: 'Deposit Amount',
      placeholder: 'Deposit Amount',
      value: form.watch('depositAmount')?.toString() || '',
    }),
    maxTermLength: select({
      handleOnChange: (_id, val) => {
        form.setValue('maxTermLength', Number(val));
        handleFormChange();
      },
      id: 'maxTermLength',
      label: 'Max. Term Length',
      options: maxTermLengths(minTermLength),
      placeholder: 'Max term length',
      value: form.watch('maxTermLength')?.toString() || '',
    }),
    minTermLength: select({
      handleOnChange: (_id, val) => {
        form.setValue('minTermLength', Number(val));
        handleFormChange();
      },
      id: 'minTermLength',
      label: 'Min. Term Length',
      options: minTermLengths,
      placeholder: 'Min term length',
      value: form.watch('minTermLength')?.toString() || '',
    }),
    zip: textInput({
      handleOnChange: (e) => {
        const val = e.target.value;
        form.setValue('zip', val);
        handleFormChange();
        trackInput('Input Interaction Zipcode');
      },
      id: 'zip',
      label: 'Zip Code',
      placeholder: 'Enter the zip code',
      value: form.watch('zip') || '',
    }),
  };

  return (
    <form
      className='flex flex-wrap items-start w-full self-stretch gap-4 py-0 px-4'
      id='cd-rates'
    >
      {inputs.map((inputForm) => {
        const input = RatesInfoInputs[inputForm];
        return (
          <div
            className='contents'
            key={`rates-input-${inputForm}`}
          >
            {input}
          </div>
        );
      })}
    </form>
  );
};
